import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export const AuthCallback: React.FC<{
  onComplete: () => void;
  onError?: () => void;
  onRecovery?: () => void;
}> = ({ onComplete, onError, onRecovery }) => {
  const { user, session } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');

  // Store the latest callbacks in refs to avoid re-running the effect
  const onCompleteRef = useRef(onComplete);
  const onRecoveryRef = useRef(onRecovery);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    if (onRecovery) {
      onRecoveryRef.current = onRecovery;
    }
  }, [onComplete, onRecovery]);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;
    let subscription: { unsubscribe: () => void } | null = null;

    const handleAuthCallback = async () => {
      try {
        // Extract hash to check for token type - DON'T clean it yet
        // Supabase needs the hash to be present to process the token via onAuthStateChange
        const hash = window.location.hash;

        if (!hash) {
          // No hash - might be a direct visit to this page
          if (mounted) {
            setStatus('error');
            setMessage('No verification token found. Please check your email and click the confirmation link we sent you.');
          }
          return;
        }

        // Check for errors in hash parameters
        const hashParams = new URLSearchParams(hash.substring(1));
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        const isRecovery = hash.includes('type=recovery');

        if (error) {
          // Clean hash after reading error
          window.history.replaceState(null, '', window.location.pathname);
          if (mounted) {
            setStatus('error');
            setMessage(errorDescription || 'Verification failed. Please try again.');
          }
          return;
        }

        // For recovery tokens, update message
        if (isRecovery && mounted) {
          setMessage('Processing password reset request...');
        }

        // Wait for onAuthStateChange to fire (Supabase processes hash tokens asynchronously)
        // Subscribe to auth state changes to detect when the session is established
        const {
          data: { subscription: authSubscription },
        } = supabase.auth.onAuthStateChange(async (event, newSession) => {
          if (!mounted) return;

          // Clear timeout since onAuthStateChange fired
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          // Clean the hash once we've processed it
          if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname);
          }

          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (newSession?.user) {
              // Check if this is a recovery token (password reset)
              if (isRecovery) {
                // Recovery token - redirect to password reset
                if (mounted && onRecoveryRef.current) {
                  onRecoveryRef.current();
                } else if (mounted) {
                  setStatus('error');
                  setMessage('Password reset callback not configured. Please contact support.');
                }
              } else {
                // Email confirmation token - check if email is confirmed
                if (newSession.user.email_confirmed_at) {
                  if (mounted) {
                    setStatus('success');
                    setMessage('Email verified successfully! Redirecting to login...');
                  }

                  // Sign out the user after email confirmation
                  // This ensures they must explicitly log in with their credentials
                  // instead of being auto-logged-in by the confirmation token
                  await supabase.auth.signOut();

                  // Wait a moment then redirect to login
                  const redirectTimeout = setTimeout(() => {
                    if (mounted) {
                      onCompleteRef.current();
                    }
                  }, 2000);
                  timeoutId = redirectTimeout;
                } else {
                  // Session exists but email not confirmed yet
                  if (mounted) {
                    setStatus('error');
                    setMessage('Your account exists but email is not yet verified. Please check your email for the confirmation link.');
                  }
                }
              }
            }
          } else if (event === 'SIGNED_OUT' || !newSession) {
            // Session was lost or invalid
            if (mounted) {
              setStatus('error');
              setMessage('Session expired or invalid. Please try again.');
            }
          }
        });
        subscription = authSubscription;

        // Set a timeout in case onAuthStateChange doesn't fire
        const timeoutIdValue = setTimeout(() => {
          if (mounted) {
            // Clean hash on timeout
            if (window.location.hash) {
              window.history.replaceState(null, '', window.location.pathname);
            }
            setStatus((prevStatus) => {
              if (prevStatus === 'loading') {
                return 'error';
              }
              return prevStatus;
            });
            setMessage('Verification timed out. Please try again.');
          }
        }, 10000); // 10 second timeout
        timeoutId = timeoutIdValue;

      } catch (err: any) {
        console.error('Auth callback error:', err);
        // Clean hash on error
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        if (mounted) {
          setStatus('error');
          setMessage(err.message || 'An error occurred during verification.');
        }
      }
    };

    handleAuthCallback();

    // Cleanup function to prevent memory leaks
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (subscription) {
        subscription.unsubscribe();
      }
    };
    // Empty dependency array - effect only runs once on mount
    // Callbacks are accessed via refs to avoid re-running on parent re-renders
  }, []);

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 animate-fade-in relative">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-zinc-900/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-900/10 blur-[120px]" />
      </div>

      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-xl p-8 shadow-2xl relative z-10 text-center">
        {status === 'loading' && (
          <>
            <div className="flex justify-center mb-6">
              <Loader2 size={48} className="text-zinc-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-medium text-white mb-2">Verifying your email</h1>
            <p className="text-zinc-500 text-[13px]">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <CheckCircle2 size={32} className="text-emerald-500" />
              </div>
            </div>
            <h1 className="text-2xl font-medium text-white mb-2">Email Verified!</h1>
            <p className="text-zinc-500 text-[13px] mb-6">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertCircle size={32} className="text-red-500" />
              </div>
            </div>
            <h1 className="text-2xl font-medium text-white mb-2">Verification Failed</h1>
            <p className="text-zinc-500 text-[13px] mb-6">{message}</p>
            <button
              onClick={() => {
                if (onError) {
                  onError();
                } else {
                  // Fallback: navigate to signup if no callback provided
                  window.location.href = '/auth/signup';
                }
              }}
              className="px-4 py-2 bg-zinc-800 text-white rounded hover:bg-zinc-700 transition-colors"
            >
              Back to Sign Up
            </button>
          </>
        )}
      </div>
    </div>
  );
};

